import { I18n } from '@byted-cg/linkchat-plugin';
import { inject, observer } from 'mobx-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  AppContextType,
  FileType,
  MESSAGE_ALL_TYPE,
} from '@byted-cg/linkchat-plugin';
import { Modal, FileCard, Button, Toast } from '@byted-cg/linkchat-ui';
import { getImageWH, getVideoWH, filterPic } from '../utils';

const utils = {
  isDoc: (name: string) => {
    const ext = ['doc', 'docx'];
    return ext.some((v) => name.indexOf(v) > -1);
  },
  isPdf: (name: string) => {
    const ext = ['pdf'];
    return ext.some((v) => name.indexOf(v) > -1);
  },
  isXls: (name: string) => {
    const ext = ['xls', 'xlsx', 'csv'];
    return ext.some((v) => name.indexOf(v) > -1);
  },
  isPpt: (name: string) => {
    const ext = ['ppt', 'pptx'];
    return ext.some((v) => name.indexOf(v) > -1);
  },
  isTxt: (name: string) => {
    const ext = ['txt'];
    return ext.some((v) => name.indexOf(v) > -1);
  },
  isMedia: (name: string) => {
    const ext = [
      'png',
      'jpg',
      'jpeg',
      'apng',
      'gif',
      'webp',
      'mp4',
      'mp3',
      'mov',
      'avi',
      'webm',
      'mkv',
      'wav',
    ];
    return ext.some((v) => name.indexOf(v) > -1);
  },
};
const getType = (fileInfo: File) => {
  const fileName = fileInfo.name;
  if (!fileName) {
    return 'Folder';
  }
  const ext = fileName.split('.')[1] || '';
  if (utils.isDoc(ext)) {
    return 'doc';
  }
  if (utils.isPdf(ext)) {
    return 'pdf';
  }
  if (utils.isXls(ext)) {
    return 'xls';
  }
  if (utils.isPpt(ext)) {
    return 'ppt';
  }
  if (utils.isTxt(ext)) {
    return 'txt';
  }
  if (utils.isMedia(ext)) {
    return 'img';
  }
  return 'Folder';
};

export type NewFile = File & {
  height?: number | string | undefined;
  width?: number | string | undefined;
  errorMessage?: number | string;
  ext?: string;
  subType?: string;
  invokeSrc?: string;
  getIcon?: any;
  headerTitle?: string;
};
interface PasteFileProps {
  appContext: AppContextType;
  files: NewFile[];
  show: boolean;
  onHandler?: () => void;
}

const PasteFile: React.FC<PasteFileProps> = ({
  files,
  appContext,
  show,
  onHandler,
}) => {
  const [loading, setLoad] = useState(false);
  const [showFileDialog, setShowFileDialog] = useState(show);
  const [previewList, setPreviewList] = useState<NewFile[]>([]);
  const [title, setTitle] = useState(
    I18n.t('linkchat_file_tile', {}, '发送文件'),
  );
  const callEnterRef = useRef<() => void>();
  callEnterRef.current = () => {
    handleSend();
  };
  const callEnterEvent = useCallback((event: any) => {
    console.log('触发了回车事件');
    let e = event || window.event;
    if (e.keyCode === 13) {
      callEnterRef && callEnterRef?.current?.();
    }
  }, []);

  const getPreviewItem = async (file: NewFile) => {
    let _type = FileType.CommonFile;
    if (file.type.includes('image')) {
      _type = FileType.Image;
    }
    if (file.type.includes('video')) {
      _type = FileType.Video;
    }
    file.subType = _type;
    file.ext = getType(file);
    file.invokeSrc = URL.createObjectURL(file);
    switch (file.subType) {
      case FileType.Image: {
        file.headerTitle = I18n.t('send_photo', {}, '发送照片');
        // 检验图片尺寸
        if (file.size > 1024 * 1024 * 300) {
          file.errorMessage = I18n.t(
            'sending_pictures_supports_a_maximum_of_300m__exceeding_the_limit!',
            {},
            '发送图片最大支持300M，超出限制！',
          );
          return file;
        }

        // 获取图片宽高
        const result = await getImageWH(file);
        if (result.isError) {
          file.errorMessage = I18n.t(
            'abnormal_image_loading',
            {},
            '图片加载异常',
          );
          URL.revokeObjectURL(file.invokeSrc);
          file.invokeSrc =
            'data:image/png;base64,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';
          return file;
        } else {
          file.height = result.height;
          file.width = result.width;
        }
        file.getIcon = (
          <div className="media-content">
            <img src={file.invokeSrc} style={{ width: '100%' }}></img>
          </div>
        );
        break;
      }
      case FileType.Video: {
        file.headerTitle = I18n.t('send_video', {}, '发送视频');
        file.getIcon = (
          <div className="media-content">
            <video src={file.invokeSrc} style={{ width: '100%' }}></video>
          </div>
        );
        // 检验视频尺寸
        if (file.size > 1024 * 1024 * 300) {
          file.errorMessage = I18n.t(
            'beyond_the_sending_limit__the_maximum_support_is_300m~',
            {},
            '超出发送限制，最大支持发送300M~',
          );
          return file;
        }

        // 获取视频宽高
        try {
          const result = await getVideoWH(file);
          file.height = result.height;
          file.width = result.width;
        } catch (error) {
          file.errorMessage = I18n.t(
            'abnormal_video_loading',
            {},
            '视频加载异常',
          );
          return file;
        }
        break;
      }
      default: {
        file.headerTitle = I18n.t('linkchat_file_tile', {}, '发送文件');
        // 检验文件尺寸
        if (file.size > 1024 * 1024 * 300) {
          file.errorMessage = I18n.t(
            'beyond_the_sending_limit__the_maximum_support_is_300m~',
            {},
            '超出发送限制，最大支持发送300M~',
          );
          return file;
        }
        break;
      }
    }
    return file;
  };

  useEffect(() => {
    if (showFileDialog) {
      Promise.all(files.map((file) => getPreviewItem(file))).then((list) => {
        if (list.length === 1) {
          setTitle(
            list[0]?.headerTitle ||
              I18n.t('linkchat_file_tile', {}, '发送文件'),
          );
        } else {
          setTitle(I18n.t('linkchat_file_tile', {}, '发送文件'));
        }
        return setPreviewList(list);
      });
    } else {
      setPreviewList([]);
    }
    callEnter(showFileDialog);
  }, [showFileDialog]);

  const handleSend = useCallback(() => {
    if (loading || !showFileDialog) return;
    setLoad(true);
    let items = previewList.filter((item) => !item.errorMessage);
    if (items && items.length) {
      items.forEach(async (item) => {
        let type = MESSAGE_ALL_TYPE.MESSAGE_TYPE_FILE;
        let PicCanBeSend = [item];
        if (item.subType === FileType.Image) {
          type = MESSAGE_ALL_TYPE.MESSAGE_TYPE_IMAGE;
          PicCanBeSend = await filterPic([item]);
        } else if (item.subType === FileType.Video) {
          type = MESSAGE_ALL_TYPE.MESSAGE_TYPE_VIDEO;
        }
        appContext?.sendMessage({
          type,
          fileArr: PicCanBeSend,
          isPasteFile: true,
        });
      });
    } else {
      Toast.show(
        I18n.t(
          'no_documents/videos/photos_to_send',
          {},
          '无可发送的文件/视频/照片',
        ),
      );
    }
    handleClose();
  }, [previewList, loading]);

  const callEnter = useCallback((tag: boolean) => {
    let event = tag ? 'addEventListener' : 'removeEventListener';
    document[event]('keydown', callEnterEvent);
    // if (tag) {
    //   document.addEventListener('keydown', callEnterEvent)
    // } else {
    //   document.removeEventListener('keydown', callEnterEvent);
    // }
  }, []);

  useEffect(() => {
    setShowFileDialog(show);
  }, [show]);

  const handleClose = useCallback(() => {
    typeof onHandler === 'function' && onHandler();
    setLoad(false);
    setShowFileDialog(false);
    setPreviewList([]);
  }, []);

  return (
    <Modal
      className="file-modal"
      header={<div className="file-modal-title">{title}</div>}
      showCloseButton
      visible={showFileDialog}
      getContainer={document.getElementsByTagName('body')[0]}
      onClose={handleClose}
      bodyClassName="modal-body"
      content={
        <>
          <div className="files-content">
            {previewList.map((item) => {
              return (
                <div className="file-item">
                  <FileCard
                    icon={item.getIcon}
                    file={item}
                    extension={item.ext}
                  ></FileCard>
                  {item.errorMessage && (
                    <div className="error-message">{item.errorMessage}</div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="bottom-btn">
            <Button
              size="small"
              onClick={handleClose}
              style={{
                marginRight: 8,
                width: 100,
                border: '1px solid #eee',
              }}
            >
              {I18n.t('close_dialog_cancel_text', {}, '取消')}
            </Button>
            <Button
              size="small"
              loading={loading}
              onClick={handleSend}
              color="primary"
              style={{
                width: 100,
              }}
            >
              {I18n.t('close_dialog_confirm_text', {}, '确认')}
            </Button>
          </div>
        </>
      }
    ></Modal>
  );
};

export default inject<{
  appContext: AppContextType;
}>(({ appContext }) => ({
  appContext,
}))(observer(PasteFile));
